import { MathAux } from '../services/math-aux/math-aux';
import { FeatureCollection, Feature, Geometry, LineString } from 'geojson';

/**
 * Define os valores de chuvas das vias.
 * Chama: centroid, getStationWeights e getPredictionRainValue
 * Chamado por: processResponse
 * 
 * @param rainData estações metereológicas com medições da chuva em um tempo específico.
 * @param interpolationTarget
 * @param p índice do radical usado no cálculo do peso das estações
 * @param rainAggregateName nome da coluna da resolução do tempo do acumulado de chuva.
 * @returns referência à feature collection com os elementos acrescidos do atributo rain_value.
 */
export function interpolateRain(
  rainData: FeatureCollection<Geometry>, 
  interpolationTarget: Feature<Geometry>[], 
  p: number, 
  rainAggregateName : string) {

  // inicializa os valores de chuva para cada via
  interpolationTarget.forEach((elem: Feature<Geometry>) => {
    let [segment_lat, segment_long] = getPoint(elem.geometry as LineString);

    // incrementa os pesos das estações com base na distância com a via atual
    let sum_weights = getStationWeights(rainData, segment_lat, segment_long, p);

    // calcula o valor de chuva para uma via (segment)
    let predicted_value = getPredictedRainValue(rainData, sum_weights, rainAggregateName);

    // valor usado para definir a cor do elemento
    elem.properties.rain_value = MathAux.round(predicted_value, 4);
  });

  return interpolationTarget;
}

/**
 * Obtém um ponto que representa a geometria da feature.
 * @param feature 
 */
function getPoint(geometry: LineString) {

  // Vias e Rios são LineString
  // Bairros são MultiPolygon
  if (geometry.type == 'LineString') {}

  return MathAux.centroid(geometry.coordinates);
}

/**
 * Calcula os pesos das estações metereológicas para as coordenadas de uma via específica.
 * Chamado por: setHighwayRainValues
 * 
 * @param rain_data estações metereológicas com medidas de índice pluviométrico em um tempo específico
 * @param segment_lat latitude da via (normalmente latitude do centróide da via)
 * @param segment_long longitude da via (normalmente longitude do centróide da via)
 * @param p índice usado da raiz usada no cálculo do peso da estação
 * @return Soma dos pesos de todas as estações para essa via.
 */
export function getStationWeights(
  rain_data: FeatureCollection<Geometry>, 
  segment_lat: number, segment_long: number, p: number): number {

  let sum_weights = 0;

  // incrementa os pesos das estações com base na distância com a via atual
  rain_data.features.forEach((station: any) => {

    // evitando as estações artificiais da fronteira interpolação, que têm id_tempo = 999999
    if (station.properties?.id_tempo != 999999) { 

      let station_lat = station.geometry.coordinates[1];
      let station_long = station.geometry.coordinates[0];

      // O valor MAX_VALUE foi incluído para evitar casos em que a estação e a via têm as mesmas
      // coordenadas, o que causava uma divisão por 0, fazendo weight assumir o valor Infinity.
      let weight = (segment_lat == station_lat && segment_long == station_long) ? 
        Number.MAX_SAFE_INTEGER: 
        1 / Math.pow(MathAux.distance(segment_lat,segment_long,station_lat,station_long),p);

      station.properties.w = weight;

      sum_weights += station.properties.w;
    }

  });

  return sum_weights;
}


  /**
   * Calcula o valor de chuva específico para uma via
   * Chamado por: setHighwayRainValues
   * 
   * @param rain_data estações metereológicas com medidas dos índices pluviais em um tempo específico.
   * @param sum_weights soma dos pesos da estação para a via
   * @return valor de chuvas previsto para a via
   */
export function getPredictedRainValue(
  rain_data: FeatureCollection<Geometry>, 
  sum_weights: number, timestamp: string): number {

  let predicted_value = 0;
  // calcula o valor de chuva para uma via (segment)
  rain_data.features.forEach((station: any) => {
    
    // verificando o id_tempo para evitar incluir as estações artificiais da fronteira da interpolação.
    if (station.properties.id_tempo != 999999) {

      // no javascript, multiplicar null por um número resulta 0. Aqui estamos  apenas explicitando
      // que pode haver registros com um acumulado de chuva igual a null.
      predicted_value += station.properties[timestamp] == null ? 0 : 
        (station.properties.w * station.properties[timestamp]) / sum_weights;
    }
  });

  return predicted_value;
};

